import type { ComputeClass, ProvisionedTask } from '../models/useTasks'
import { formatDuration, taskLifecycle, taskShapeLabel, type TaskState } from './taskDeadline.ts'

/**
 * Which nodes have compute attached right now (app#329).
 *
 * A node "has compute" while it has an OPEN task: `tasks.node_id = node.id` and `completed_at` is
 * null. That is the only link between the two tables — the node row does not know it is a compute
 * node (router REVIEW-billing-compute-ux-2026-09-14 § 5). No new column and no new read: this runs
 * over the rows `models/useTasks` already reads for the Provisioned machines table.
 *
 * ⚠⚠ A STOPPED COMPUTE NODE IS AN ORDINARY OFFLINE NODE, AND IT GETS NO BADGE. Stopping a machine
 * stamps `completed_at` and leaves the node row, with its name, labels and storage (router#1078).
 * A badge keyed on "this node ever ran a task" would mark every such node for ever, and say it has
 * compute when nothing is running or billing.
 *
 * ⚠ A node re-provisioned under the same name registers again under the same `node_id`, but a
 * RE-ADOPTED task does not set `tasks.node_id` until router#1065 ships. Until then such a node has
 * no open task that names it, so it shows no badge. That is a missing badge, not a wrong one.
 *
 * ⚠ This module imports types only (plus `taskDeadline`, which does the same), so a plain
 * `node --experimental-strip-types` test can reach it without a Supabase client.
 */

export type NodeCompute = {
  taskId: string
  nodeId: string
  /** The class name (`small`), or the shape when it matches no class — see `taskShapeLabel`. */
  shape: string
  /** `running`, or `expiring` when the deadline has passed and the sweeper has not stopped it yet. */
  state: Exclude<TaskState, 'ended'>
  /** Milliseconds to the deadline, negative once past it. Null when the task has no deadline. */
  remainingMs: number | null
  /** One line for a tooltip: `Compute · small · stops in 21h 40m`. */
  label: string
}

/**
 * The tail of the label: when the machine stops.
 *
 * ⚠ NULL IS "NO DEADLINE", NOT "NOW" — the same rule `taskLifecycle` carries. A task created before
 * router migration 0152, or with `max_hours: 0`, runs until somebody stops it.
 */
const stopsText = (state: NodeCompute['state'], remainingMs: number | null): string => {
  if (state === 'expiring') return 'stopping'
  if (remainingMs === null) return 'no time limit'
  return `stops in ${formatDuration(remainingMs)}`
}

export const computeLabel = (shape: string, state: NodeCompute['state'], remainingMs: number | null): string =>
  `Compute · ${shape} · ${stopsText(state, remainingMs)}`

/**
 * The compute attached to each node, keyed by node id.
 *
 * ⚠ ONLY OPEN TASKS WITH A NODE. A task whose container has not registered yet has no `node_id`,
 * and there is no node on screen to put a badge on.
 *
 * ⚠ If a node has more than one open task, the NEWEST wins. The router refuses to re-adopt a name
 * whose task is still open, so two should not exist; if a race writes them anyway, the newest is
 * the container the node is running as, and the choice must not depend on the order of the rows.
 */
export const openComputeByNode = (
  tasks: ProvisionedTask[],
  classes: ComputeClass[],
  now: Date,
): Map<string, NodeCompute> => {
  const newest = new Map<string, ProvisionedTask>()
  tasks.forEach(task => {
    if (task.completedAt !== null || task.nodeId === null) return
    const held = newest.get(task.nodeId)
    if (!held || Date.parse(task.createdAt) > Date.parse(held.createdAt)) newest.set(task.nodeId, task)
  })

  const result = new Map<string, NodeCompute>()
  newest.forEach((task, nodeId) => {
    const lifecycle = taskLifecycle(task, now)
    // `completedAt` is null here, so the lifecycle cannot be `ended`.
    const state = lifecycle.state === 'expiring' ? 'expiring' : 'running'
    const shape = taskShapeLabel(task, classes)
    result.set(nodeId, {
      taskId: task.id,
      nodeId,
      shape,
      state,
      remainingMs: lifecycle.remainingMs,
      label: computeLabel(shape, state, lifecycle.remainingMs),
    })
  })
  return result
}

/**
 * Whether any task is still open, which is what decides whether the badges need re-reading.
 * A list of finished tasks cannot put a badge on screen, so nothing is left to poll for.
 */
export const hasOpenTask = (tasks: ProvisionedTask[]): boolean => tasks.some(task => task.completedAt === null)
